import React, { useEffect, useMemo, useState } from 'react';

import PageWrapper from './PageWrapper';
import Page from './Page';
import { RouteComponentProps } from 'react-router';
import { BrandLogo } from '@go-client/components/UIkit/Brand/BrandLogo/BrandLogo';

import './GroupsHome.scss';
import { PropertyBanner } from '@go-client/components/UIkit/Property/PropertyBanner';
import { MenuBar } from '@go-client/components';
import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import {
  BubbleAccordion,
  BubbleButton,
  BubbleDrawer,
  BubbleFormGroup,
  BubbleInput,
  BubbleOverlay,
  BubbleTable,
} from '@go-client/components/BubbleUI';
import { BubbleTableFields, BubbleTableTemplate } from '@go-client/components/BubbleUI/BubbleTable';
import {
  exportGroups,
  getSimpleGroupBookingsByProperty,
  setArchived,
} from '@go-client/lib/api/groupBooking';
import { BareSimpleGroupBooking } from '@go-shared/types/groupBooking';
import { useUserInfo } from '@go-client/context/UserInfoContext';
import Fuse from 'fuse.js';
import { DEFAULT_FUSE_SETTINGS } from '@go-client/lib/fuse';
import { canArchiveGroups, canCreateOrEditBookings } from '@go-shared/lib/permissions';
import GroupsNavigation from '@go-client/components/Navigations/GroupsNavigation';
import { camelToSentenceCase } from '@go-shared/lib/string';
import { GroupsMode } from './GroupsHome';
import GroupBookingForm from '@go-client/components/GroupBookingForm/GroupBookingForm';
import { useServerInfo } from '@go-client/context/ServerInfoContext';
import { GroupParty } from '@go-shared/types/groupParty';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

type GroupsPartyTableItem = Omit<GroupParty, 'phone' | 'email' | 'files' | '_id' | 'groupType'> & {
  id: string;
  groupType?: string;
  _group: BareSimpleGroupBooking;
};

type GroupsTableFields = BubbleTableFields<GroupsPartyTableItem> & {
  _visible?: boolean;
  _toggleable?: boolean;
};

interface GroupsPartyParams {
  property: string;
}

type GroupsPartyDataBundle = {
  simpleGroupBookings: BareSimpleGroupBooking[];
};

interface GroupsPartyFilters {
  visibleFields: { [key: string]: boolean };
}

type GroupsPartyPageProgs = RouteComponentProps<GroupsPartyParams>;

const GroupsHome: React.FC<GroupsPartyPageProgs> = (props: GroupsPartyPageProgs) => {
  const serverInfo = useServerInfo();
  const { activeProperty } = useActiveProperty();
  const { user } = useUserInfo();
  const [mode, setMode] = useState<GroupsMode>({ type: 'default' });

  const DEFAULT_FILTERS = {
    visibleFields: {
      name: true,
      groupId: true,
      groupType: true,
      contact: true,
      size: true,
      salesOffice: true,
      embark: true,
      debark: true,
      cabinDrops: false,
      diningPreference: false,
      guestExperience: false,
      prepaidPackages: false,
      groupNotes: false,
    },
  };

  const [filters, setFilters] = useState<GroupsPartyFilters>({
    ...DEFAULT_FILTERS,
  });

  const [dataBundle, setDataBundle] = useState<GroupsPartyDataBundle>({
    simpleGroupBookings: [],
  });

  const [showArchived, setShowArchived] = useState(false);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [isBusy, setIsBusy] = useState(false);

  useEffect(() => {
    fetchData().catch(console.error);
  }, [activeProperty]);

  async function fetchData() {
    setLoading(true);

    try {
      const simpleGroupBookings = await getSimpleGroupBookingsByProperty(activeProperty._id);
      simpleGroupBookings.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      setDataBundle({ simpleGroupBookings });
      return simpleGroupBookings;
    } finally {
      setLoading(false);
    }
  }

  const fuse = useMemo(() => {
    return new Fuse(dataBundle.simpleGroupBookings, {
      ...DEFAULT_FUSE_SETTINGS,
      keys: ['party.name'],
    });
  }, [dataBundle.simpleGroupBookings]);

  const fuseSearchResults = useMemo(() => {
    if (!search) return undefined;
    return fuse.search(search).map(s => s.item);
  }, [fuse, search]);

  const groups = fuseSearchResults || dataBundle.simpleGroupBookings;

  const tableItems: GroupsPartyTableItem[] = groups
    .filter(g => showArchived || !g.archived)
    .map(g => ({
      id: g._id,
      name: g.party.name,
      groupId: g.party.groupId,
      groupType: g.party.groupType?.name,
      contact: g.party.contact,
      size: g.party.size,
      salesOffice: g.salesOffice,
      embark: g.party.embark,
      debark: g.party.debark,
      cabinDrops: g.party.cabinDrops,
      diningPreference: g.party.diningPreference,
      guestExperience: g.party.guestExperience,
      prepaidPackages: g.party.prepaidPackages,
      notes: g.party.notes,
      groupGuideName: g.party.groupGuideName,
      groupGuideCabin: g.party.groupGuideCabin,
      hospitalityDesk: g.party.hospitalityDesk,
      _group: g,
    }));

  const tableFields: GroupsTableFields[] = [
    {
      key: 'name',
      _toggleable: true,
    },
    {
      key: 'groupId',
      label: 'Id',
      _toggleable: true,
    },
    {
      key: 'groupType',
      label: 'Type',
      _toggleable: true,
    },
    {
      key: 'contact',
      _toggleable: true,
    },
    {
      key: 'size',
      label: 'Guests',
      _toggleable: true,
    },
    {
      key: 'groupGuideName',
      label: 'Tour Leader',
      _toggleable: true,
    },
    {
      key: 'groupGuideCabin',
      label: 'TL Cabin / Booking No.',
      _toggleable: true,
    },
    {
      key: 'salesOffice',
      _toggleable: true,
    },
    {
      key: 'embark',
      tdProps: { className: 'whitespace-nowrap' },
      _toggleable: true,
    },
    {
      key: 'debark',
      tdProps: { className: 'whitespace-nowrap' },
      _toggleable: true,
    },
    {
      key: 'cabinDrops',
      _toggleable: true,
    },
    {
      key: 'diningPreference',
      _toggleable: true,
    },
    {
      key: 'guestExperience',
      _toggleable: true,
    },
    {
      key: 'prepaidPackages',
      _toggleable: true,
    },
    {
      key: 'notes',
      _toggleable: true,
    },
    {
      key: 'hospitalityDesk',
      label: 'Hosp Desk Req.',
      _toggleable: true,
    },
    {
      key: 'actions',
      label: '',
      thProps: { className: 'pl-4-i' },
      tdProps: { className: 'whitespace-nowrap p-1-i pr-4-i text-right' },
      _visible: true,
    },
  ];

  const tableTemplates: Array<BubbleTableTemplate<GroupsPartyTableItem>> = [
    {
      key: 'hospitalityDesk',
      render: data => (
        <FontAwesomeIcon
          icon={data.hospitalityDesk ? 'check' : 'times'}
          className={data.hospitalityDesk ? 'text-lime' : 'text-crimson'}
        />
      ),
    },
    {
      key: 'actions',
      render: data => (
        <>
          {canCreateOrEditBookings(user) && (
            <BubbleButton
              onClick={() => setMode({ type: 'group-edit', groupId: data._group._id })}
              className="p-2-i"
              variant="navy"
              inverse
              icon="edit"
            />
          )}
          {canArchiveGroups(user) && (
            <BubbleButton
              onClick={() => handleArchiveGroup(data._group)}
              className="p-2-i"
              variant="dirt"
              inverse
              icon="archive"
            />
          )}
        </>
      ),
    },
  ];

  function handleCloseGroupBookingForm() {
    setMode({ type: 'default' });
  }

  async function handleExportGroupBookings() {
    setIsBusy(true);

    try {
      await exportGroups(activeProperty._id);
    } finally {
      setIsBusy(false);
    }
  }

  async function handleArchiveGroup(group: BareSimpleGroupBooking) {
    await setArchived(group._id, !group.archived);
    await fetchData();
  }

  const visibleTableFields = tableFields.filter(tf =>
    tf._visible !== undefined ? tf._visible : filters.visibleFields[tf.key]
  );
  const toggleableTableFields = tableFields.filter(tf => tf._toggleable);

  return (
    <PageWrapper {...props}>
      <Page className="Groups absolute flex items-stretch">
        <MenuBar {...props} property={activeProperty} />
        <div className="SideMenu bg-forest-darkest flex flex-col">
          <div className="bg-white flex flex-col items-center">
            <BrandLogo logo={activeProperty.brand.logo} />
            <PropertyBanner {...props} property={activeProperty} swappable />
          </div>
          <div className={`flex flex-col relative items-stretch p-4 bg-forest-darker`}>
            <div className="grid gap-2">
              {canCreateOrEditBookings(user) && (
                <BubbleButton
                  variant="lime"
                  className="text-left"
                  block
                  onClick={() => setMode({ type: 'group-edit' })}
                  icon="plus"
                >
                  New Group
                </BubbleButton>
              )}
              <BubbleOverlay loading={isBusy} loadingClassName="rounded" className="flex-grow">
                <BubbleButton
                  variant="slate"
                  className="text-left"
                  block
                  icon="file-export"
                  onClick={handleExportGroupBookings}
                >
                  Export Groups
                </BubbleButton>
              </BubbleOverlay>
            </div>
          </div>
          <BubbleAccordion
            variant="forest"
            className="flex-grow flex flex-col border-t border-forest-darkest"
            labels={['Filters']}
            open={[0]}
            locked={[0]}
          >
            <div className="grid gap-4">
              <div className="text-white flex relative pt-4 px-4">
                <BubbleInput
                  type="checkbox"
                  variant="forest"
                  className="mr-1"
                  inputClassName="stretched-link"
                  checked={showArchived}
                  onValue={checked => {
                    setShowArchived(checked);
                  }}
                />
                <span>Show Archived</span>
              </div>
              <BubbleFormGroup label="Fields" labelClassName="text-white-i" className="px-4 pb-4">
                <div className="grid grid-cols-1">
                  {toggleableTableFields.map(tf => (
                    <div key={tf.key} className="text-white flex relative py-1">
                      <BubbleInput
                        type="checkbox"
                        variant="forest"
                        className="mr-1"
                        inputClassName="stretched-link"
                        checked={filters.visibleFields[tf.key]}
                        onValue={chk =>
                          setFilters({
                            ...filters,
                            visibleFields: { ...filters.visibleFields, [tf.key]: chk },
                          })
                        }
                      />
                      <span>{tf.label || camelToSentenceCase(tf.key)}</span>
                    </div>
                  ))}
                </div>
              </BubbleFormGroup>
            </div>
          </BubbleAccordion>
        </div>
        <div className="GroupsContent bg-blue-lightest overflow-y-auto flex-grow">
          <div
            className="GroupsHeader align-with-logo flex items-end px-5 sticky top-0
              left-0 z-10 bg-blue-lightest"
          >
            <div className="mb-3 w-full flex items-end px-3">
              <GroupsNavigation active="groups"></GroupsNavigation>
            </div>
          </div>
          <div className="GroupsBody mx-5 content-minus-logo min-w-900">
            <BubbleOverlay loading={loading}>
              <BubbleTable
                responsive
                striped
                onRowDblClick={data => {
                  if (canCreateOrEditBookings(user)) {
                    setMode({ type: 'group-edit', groupId: data._group._id });
                  }
                }}
                headVariant="forest"
                fields={visibleTableFields}
                items={tableItems}
                keyExtractor={item => item.id}
                stickyHeader={130}
                templates={tableTemplates}
                showPretitle
                pretitleClassName="p-0-i"
                pretitleRender={() => (
                  <div className="px-4 pt-4 pb-1 flex items-center">
                    <div className="flex-grow">
                      <BubbleInput
                        variant="forest"
                        inputClassName="outline-none-i"
                        type="text"
                        block
                        value={search}
                        onValue={setSearch}
                        placeholder="Search..."
                      />
                    </div>
                  </div>
                )}
              />
            </BubbleOverlay>
            <div className="mt-3">&nbsp;</div>
          </div>
        </div>
        {mode.type !== 'default' && (
          <BubbleDrawer hideTools containerClassName="bg-eggshell">
            <GroupBookingForm
              mode={mode}
              property={activeProperty}
              user={user}
              groupTypes={serverInfo.brand.groupTypes}
              onClose={handleCloseGroupBookingForm}
              onSubmitGroupBookings={fetchData}
            />
          </BubbleDrawer>
        )}
      </Page>
    </PageWrapper>
  );
};

export default GroupsHome;
